import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Button,
  Autocomplete,
  TextInput,
  Select,
  Stack,
  Textarea,
  Group,
  Switch,
  Text,
  Loader,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconCalendar,
  IconBuilding,
  IconBuildingStore,
  IconId,
  IconBolt,
  IconBuildingCommunity,
} from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import api from '../../api/axiosConfig';
import dayjs from 'dayjs';

// Türkçe karakter farklılıklarını ve gereksiz boşlukları dikkate alarak karşılaştırır.
const normalizeLocation = (value = '') =>
  String(value)
    .trim()
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ı/g, 'i');

export default function AddSubscriptionModal({ opened, close, onSuccess, recordToEdit }) {
  const [loading, setLoading] = useState(false);
  const [existingLocations, setExistingLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const form = useForm({
    initialValues: {
      branch_name: '',
      sub_type: 'Elektrik',
      provider: '',
      subscription_no: '',
      start_date: new Date(),
      is_active: true,
      notes: '',
    },
    validate: {
      branch_name: (val) => (!val?.trim() ? 'Şube adı zorunludur' : null),
      provider: (val) => (!val?.trim() ? 'Kurum adı zorunludur' : null),
      subscription_no: (val) => (!val?.trim() ? 'Abonelik numarası zorunludur' : null),
    },
  });

  useEffect(() => {
    if (recordToEdit) {
      form.setValues({
        branch_name: recordToEdit.branch_name || '',
        sub_type: recordToEdit.sub_type || 'Elektrik',
        provider: recordToEdit.provider || '',
        subscription_no: recordToEdit.subscription_no || '',
        start_date: recordToEdit.start_date ? new Date(recordToEdit.start_date) : null,
        is_active: Boolean(recordToEdit.is_active),
        notes: recordToEdit.notes || '',
      });
    } else if (opened) {
      form.reset();
      form.setValues({
        branch_name: '',
        sub_type: 'Elektrik',
        provider: '',
        subscription_no: '',
        start_date: new Date(),
        is_active: true,
        notes: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordToEdit, opened]);

  // Modal açıldığında daha önce kaydedilmiş aboneliklerdeki şubeleri getirir.
  // Mevcut GET /subscriptions/ endpoint'ini kullanır; backend değişikliği gerekmez.
  useEffect(() => {
    if (!opened) return undefined;

    let cancelled = false;

    const fetchLocations = async () => {
      setLocationsLoading(true);
      try {
        const response = await api.get('/subscriptions/');
        if (!cancelled) {
          const records = Array.isArray(response.data) ? response.data : [];
          setExistingLocations(records.map((record) => record.branch_name));
        }
      } catch (error) {
        console.error('Önceki lokasyonlar yüklenemedi:', error);
        if (!cancelled) setExistingLocations([]);
      } finally {
        if (!cancelled) setLocationsLoading(false);
      }
    };

    fetchLocations();

    return () => {
      cancelled = true;
    };
  }, [opened]);

  // Aynı lokasyonun birden fazla aboneliği olsa bile bir kez göster.
  const locationSuggestions = useMemo(() => {
    const unique = new Map();

    existingLocations.forEach((name) => {
      if (typeof name !== 'string') return;
      const cleanName = name.trim();
      const key = normalizeLocation(cleanName);
      if (key && !unique.has(key)) unique.set(key, cleanName);
    });

    return [...unique.values()].sort((a, b) => a.localeCompare(b, 'tr'));
  }, [existingLocations]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        branch_name: values.branch_name.trim(),
        start_date: values.start_date ? dayjs(values.start_date).format('YYYY-MM-DD') : null,
      };

      if (recordToEdit) {
        await api.put(`/subscriptions/${recordToEdit.id}`, payload);
      } else {
        await api.post('/subscriptions/', payload);
      }

      await onSuccess?.();
      close();
      form.reset();
    } catch (error) {
      console.error('Kayıt hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={
        <Group gap="sm">
          <IconBuildingCommunity size={20} />
          <div>
            <Text fw={800}>
              {recordToEdit ? 'Aboneliği Düzenle' : 'Yeni Abonelik Ekle'}
            </Text>
            <Text size="xs" c="dimmed">
              Şube ve hizmet detaylarını eksiksiz gir
            </Text>
          </div>
        </Group>
      }
      centered
      radius="xl"
      size="lg"
      overlayProps={{ blur: 3 }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Autocomplete
            label="Şube / Tesis Adı"
            placeholder="Örn: Merkez Ofis, Depo, Kadıköy Şube"
            description="Daha önce kaydedilen lokasyonlar yazarken önerilir; yeni bir ad da girebilirsiniz."
            leftSection={<IconBuilding size={16} />}
            rightSection={locationsLoading ? <Loader size="xs" /> : undefined}
            data={locationSuggestions}
            filter={({ options, search }) => {
              const query = normalizeLocation(search);
              return options
                .filter((option) => normalizeLocation(option.label).includes(query))
                .slice(0, 8);
            }}
            limit={8}
            maxDropdownHeight={220}
            nothingFoundMessage={null}
            required
            radius="md"
            {...form.getInputProps('branch_name')}
          />

          <Group grow align="flex-start">
            <Select
              label="Abonelik Türü"
              data={[
                { value: 'Elektrik', label: 'Elektrik' },
                { value: 'Su', label: 'Su' },
                { value: 'Doğalgaz', label: 'Doğalgaz' },
                { value: 'İnternet', label: 'İnternet' },
                { value: 'Telefon / Santral', label: 'Telefon / Santral' },
                { value: 'Diğer', label: 'Diğer' },
              ]}
              leftSection={<IconBolt size={16} />}
              required
              radius="md"
              {...form.getInputProps('sub_type')}
            />

            <DatePickerInput
              label="Sözleşme / Başlangıç Tarihi"
              placeholder="Tarih seç"
              valueFormat="DD.MM.YYYY"
              leftSection={<IconCalendar size={16} />}
              radius="md"
              {...form.getInputProps('start_date')}
            />
          </Group>

          <Group grow align="flex-start">
            <TextInput
              label="Kurum / Sağlayıcı"
              placeholder="Örn: Enerjisa, İSKİ, Turkcell"
              leftSection={<IconBuildingStore size={16} />}
              required
              radius="md"
              {...form.getInputProps('provider')}
            />

            <TextInput
              label="Abonelik / Tesisat No"
              placeholder="Örn: 100456789"
              leftSection={<IconId size={16} />}
              required
              radius="md"
              {...form.getInputProps('subscription_no')}
            />
          </Group>

          <Textarea
            label="Açıklama / Notlar"
            placeholder="Sözleşme şartları, tarife detayları, özel notlar..."
            minRows={3}
            radius="md"
            {...form.getInputProps('notes')}
          />

          <Switch
            label="Abonelik aktif durumda"
            checked={form.values.is_active}
            onChange={(event) =>
              form.setFieldValue('is_active', event.currentTarget.checked)
            }
            color="blue"
            mt="xs"
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" radius="xl" onClick={close} type="button">
              Vazgeç
            </Button>
            <Button type="submit" radius="xl" loading={loading}>
              {recordToEdit ? 'Güncelle' : 'Kaydet'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
